import { CheckCircle, Loader2, Pencil, Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PromptSetFormModal } from '@/components/prompt-sets/PromptSetFormModal';
import { cn } from '@/lib/utils';
import { createPromptSet, listPromptSets, updatePromptSet } from '@/services/promptSetService';

import type { PromptSet, PromptSetFormData } from '@/types/promptSet.types';

type ModalState =
  | { isOpen: false }
  | { isOpen: true; mode: 'create' }
  | { isOpen: true; mode: 'edit'; promptSet: PromptSet };

export function PromptSetsPage() {
  const { t } = useTranslation();
  const [promptSets, setPromptSets] = useState<PromptSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState>({ isOpen: false });

  const loadPromptSets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listPromptSets();
      setPromptSets(data);
    } catch {
      setError(t('promptSets.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadPromptSets();
  }, [loadPromptSets]);

  const handleSave = useCallback(
    async (data: PromptSetFormData) => {
      try {
        if (modal.isOpen && modal.mode === 'edit') {
          await updatePromptSet(modal.promptSet.promptSetId, data);
          toast.success(t('promptSets.toasts.updateSuccess'));
        } else {
          await createPromptSet(data);
          toast.success(t('promptSets.toasts.createSuccess'));
        }
        setModal({ isOpen: false });
        await loadPromptSets();
      } catch {
        toast.error(t('promptSets.toasts.error'));
      }
    },
    [modal, loadPromptSets, t],
  );

  const handleActivate = useCallback(
    async (promptSet: PromptSet) => {
      try {
        await updatePromptSet(promptSet.promptSetId, { active: true });
        toast.success(t('promptSets.toasts.activateSuccess'));
        await loadPromptSets();
      } catch {
        toast.error(t('promptSets.toasts.error'));
      }
    },
    [loadPromptSets, t],
  );

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>{t('promptSets.title')}</h1>
          <p className='mt-1 text-sm text-gray-500'>{t('promptSets.description')}</p>
        </div>
        <button
          onClick={() => setModal({ isOpen: true, mode: 'create' })}
          className='inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'>
          <Plus className='h-4 w-4' />
          {t('promptSets.create')}
        </button>
      </div>

      {/* Error */}
      {error && <div className='rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700'>{error}</div>}

      {/* Loading */}
      {isLoading && !error && (
        <div className='flex justify-center py-12'>
          <Loader2 className='h-8 w-8 animate-spin text-blue-600' />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && promptSets.length === 0 && (
        <div className='rounded-lg border border-gray-200 bg-white p-12 text-center'>
          <p className='text-sm text-gray-500'>{t('promptSets.empty')}</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && promptSets.length > 0 && (
        <div className='overflow-hidden rounded-lg bg-white shadow'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.name')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.questions')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.retakePolicy')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.maxDuration')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.active')}
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.created')}
                </th>
                <th className='px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500'>
                  {t('promptSets.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200 bg-white'>
              {promptSets.map((ps) => (
                <tr key={ps.promptSetId} className='hover:bg-gray-50'>
                  <td className='whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900'>{ps.name}</td>
                  <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>{ps.prompts.length}</td>
                  <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                    {t(`promptSets.retakePolicies.${ps.retakePolicy}`)}
                  </td>
                  <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>{ps.maxDurationSec}s</td>
                  <td className='whitespace-nowrap px-6 py-4'>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                        ps.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600',
                      )}>
                      {ps.active ? t('promptSets.status.active') : t('promptSets.status.inactive')}
                    </span>
                  </td>
                  <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                    {new Date(ps.createdAt).toLocaleDateString()}
                  </td>
                  <td className='whitespace-nowrap px-6 py-4 text-right'>
                    <div className='flex items-center justify-end gap-2'>
                      <button
                        onClick={() => setModal({ isOpen: true, mode: 'edit', promptSet: ps })}
                        className='inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50'>
                        <Pencil className='h-3.5 w-3.5' />
                        {t('promptSets.table.edit')}
                      </button>
                      {!ps.active && (
                        <button
                          onClick={() => void handleActivate(ps)}
                          className='inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50'>
                          <CheckCircle className='h-3.5 w-3.5' />
                          {t('promptSets.table.activate')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      <PromptSetFormModal
        isOpen={modal.isOpen}
        onClose={() => setModal({ isOpen: false })}
        onSave={handleSave}
        mode={modal.isOpen ? modal.mode : 'create'}
        promptSet={modal.isOpen && modal.mode === 'edit' ? modal.promptSet : undefined}
        promptSetId={modal.isOpen && modal.mode === 'edit' ? modal.promptSet.promptSetId : undefined}
      />
    </div>
  );
}
